import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
    PatientControllerv2Service,
    ReimbursementRequestControllerV2Service,
    StipendRequestControllerV2Service,
    TravelRequestControllerV2Service,
} from '@app/generated-api';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { MdsIconButtonComponent, MdsTextareaComponent } from '@medpacesoftwaredevelopment/designsystem';
import { DateFormat } from '@models/date-format';
import { AdminSiteServices } from '@services/admin/admin-site.service';
import { take, tap } from 'rxjs';
import { NoteCardContext } from '../note-card-context.enum';
import { NoteCardElementFormGroup } from '../note-card.formgroup';

@Component({
    selector: 'medpace-request-side-notes-item',
    imports: [MdsIconButtonComponent, MdsTextareaComponent, dateFormatPipe, UpperCasePipe],
    templateUrl: './request-side-notes-item.component.html',
    styleUrl: './request-side-notes-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestSideNotesItemComponent {
    patientService = inject(PatientControllerv2Service);
    siteService = inject(AdminSiteServices);
    reimbursementService = inject(ReimbursementRequestControllerV2Service);
    stipendService = inject(StipendRequestControllerV2Service);
    travelService = inject(TravelRequestControllerV2Service);

    noteFormGroup = input.required<NoteCardElementFormGroup>();
    noteIndex = input.required<number>();
    datetime12 = DateFormat.dateTime12;
    //type of the note (e.g., Patient, Site, Reimbursement, Stipend, Travel)
    context = input<NoteCardContext | null>(null);
    //id of the parent note object
    objectId = input.required<number | undefined>();

    removeNoteEvent = output<number>();

    isRequestContext = computed(() => {
        return (
            this.context() === NoteCardContext.Reimbursement ||
            this.context() === NoteCardContext.Travel ||
            this.context() === NoteCardContext.Stipend
        );
    });

    private editingToggled = signal(false);

    noteValueModel = computed(() => {
        if (!this.noteFormGroup().valueModel()) return null;
        return this.noteFormGroup().valueModel();
    });

    //new (unsaved) notes always start in edit mode
    isEditing = computed(() => {
        return this.noteValueModel()?.id === 0 || this.editingToggled();
    });

    noteContent = signal<{ noteId: number; note: string } | undefined>(undefined);

    editNote() {
        if (!this.isEditing()) {
            this.editingToggled.set(true);
            return;
        } else if (!this.noteFormGroup()!.valid) {
            this.noteFormGroup()!.markAsTouched();
            return;
        }
        let noteModel = this.noteFormGroup()!.getModelFromValue();

        this.noteContent.set({
            noteId: noteModel.id,
            note: noteModel.note,
        });
    }

    editPatientNote = rxResource({
        params: () => {
            const itemId = this.objectId();
            const context = this.context();
            const content = this.noteContent();

            if (!itemId || context !== NoteCardContext.Patient || !content) return undefined;

            return { itemId, content };
        },
        stream: ({ params }) => {
            return this.patientService
                .patientControllerv2UpdatePatientSingleNote(params.itemId, params.content.noteId, {
                    note: params.content.note,
                })
                .pipe(
                    take(1),
                    tap((note) => {
                        this.noteFormGroup().setValueFromModel(note);
                        this.editingToggled.set(false);
                    })
                );
        },
    });

    editSiteNote = rxResource({
        params: () => {
            const itemId = this.objectId();
            const context = this.context();
            const content = this.noteContent();

            if (!itemId || context !== NoteCardContext.Site || !content) return undefined;

            return { itemId, content };
        },
        stream: ({ params }) => {
            return this.siteService.updateSiteNote(params.itemId, params.content.noteId, params.content.note).pipe(
                take(1),
                tap((note) => {
                    this.noteFormGroup().setValueFromModel(note);
                    this.editingToggled.set(false);
                })
            );
        },
    });

    createEditReimbursementNote = rxResource({
        params: () => {
            const itemId = this.objectId();
            const context = this.context();
            const content = this.noteContent();

            if (!itemId || context !== NoteCardContext.Reimbursement || !content) return undefined;

            return { itemId, content };
        },
        stream: ({ params }) => {
            const request$ =
                params.content.noteId === 0
                    ? this.reimbursementService.reimbursementRequestControllerV2CreateRequestSingleNote(params.itemId, {
                          note: params.content.note,
                      })
                    : this.reimbursementService.reimbursementRequestControllerV2UpdateRequestSingleNote(
                          params.itemId,
                          params.content.noteId,
                          { note: params.content.note }
                      );

            return request$.pipe(
                take(1),
                tap((note) => {
                    this.noteFormGroup().setValueFromModel(note);
                    this.editingToggled.set(false);
                })
            );
        },
    });

    createEditStipendNote = rxResource({
        params: () => {
            const itemId = this.objectId();
            const context = this.context();
            const content = this.noteContent();

            if (!itemId || context !== NoteCardContext.Stipend || !content) return undefined;

            return { itemId, content };
        },
        stream: ({ params }) => {
            const request$ =
                params.content.noteId === 0
                    ? this.stipendService.stipendRequestControllerV2CreateRequestSingleNote(params.itemId, {
                          note: params.content.note,
                      })
                    : this.stipendService.stipendRequestControllerV2UpdateRequestSingleNote(
                          params.itemId,
                          params.content.noteId,
                          { note: params.content.note }
                      );

            return request$.pipe(
                take(1),
                tap((note) => {
                    this.noteFormGroup().setValueFromModel(note);
                    this.editingToggled.set(false);
                })
            );
        },
    });

    createEditTravelNote = rxResource({
        params: () => {
            const itemId = this.objectId();
            const context = this.context();
            const content = this.noteContent();

            if (!itemId || context !== NoteCardContext.Travel || !content) return undefined;

            return { itemId, content };
        },
        stream: ({ params }) => {
            const request$ =
                params.content.noteId === 0
                    ? this.travelService.travelRequestControllerV2CreateRequestSingleNote(params.itemId, {
                          note: params.content.note,
                      })
                    : this.travelService.travelRequestControllerV2UpdateRequestSingleNote(
                          params.itemId,
                          params.content.noteId,
                          { note: params.content.note }
                      );

            return request$.pipe(
                take(1),
                tap((note) => {
                    this.noteFormGroup().setValueFromModel(note);
                    this.editingToggled.set(false);
                })
            );
        },
    });

    noteIdToRemove = signal<number | null>(null);

    removeNote(noteId: number) {
        //new notes are not persisted yet, so they only need to be removed from the form
        if (noteId === 0) {
            this.removeNoteEvent.emit(noteId);
            return;
        }

        this.noteIdToRemove.set(noteId);
    }

    removeReimbursementNote = rxResource({
        params: () => {
            const itemId = this.objectId();
            const noteIdToRemove = this.noteIdToRemove();
            const context = this.context();

            if (!itemId || !noteIdToRemove || context !== NoteCardContext.Reimbursement) return undefined;

            return { itemId, noteIdToRemove };
        },
        stream: ({ params }) =>
            this.reimbursementService
                .reimbursementRequestControllerV2DeleteRequestSingleNote(params.itemId, params.noteIdToRemove)
                .pipe(
                    take(1),
                    tap((_) => {
                        this.removeNoteEvent.emit(params.noteIdToRemove);
                    })
                ),
    });

    removeStipendNote = rxResource({
        params: () => {
            const itemId = this.objectId();
            const noteIdToRemove = this.noteIdToRemove();
            const context = this.context();

            if (!itemId || !noteIdToRemove || context !== NoteCardContext.Stipend) return undefined;

            return { itemId, noteIdToRemove };
        },
        stream: ({ params }) =>
            this.stipendService
                .stipendRequestControllerV2DeleteRequestSingleNote(params.itemId, params.noteIdToRemove)
                .pipe(
                    take(1),
                    tap((_) => {
                        this.removeNoteEvent.emit(params.noteIdToRemove);
                    })
                ),
    });

    removeTravelNote = rxResource({
        params: () => {
            const itemId = this.objectId();
            const noteIdToRemove = this.noteIdToRemove();
            const context = this.context();

            if (!itemId || !noteIdToRemove || context !== NoteCardContext.Travel) return undefined;

            return { itemId, noteIdToRemove };
        },
        stream: ({ params }) =>
            this.travelService
                .travelRequestControllerV2DeleteRequestSingleNote(params.itemId, params.noteIdToRemove)
                .pipe(
                    take(1),
                    tap((_) => {
                        this.removeNoteEvent.emit(params.noteIdToRemove);
                    })
                ),
    });
}
